import { toast } from "react-toastify";
import { endIdleSession, isRecordingHeld, markSessionExpiredOnLoad } from "./sessionExpiry";
import { clearSessionToken, peekSessionToken, tokenStorage } from "./sessionToken";

// Kept next to the token so tabs sharing a token also share activity.
const LAST_ACTIVITY_KEY = "lastActivityAt";
// `scroll` doesn't bubble, but the capture listener on document still sees it, so reading a
// long note by scrolling counts.
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
  "visibilitychange",
] as const;
const ACTIVITY_THROTTLE_MS = 1000;
const WRITE_THROTTLE_MS = 15 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;

/** How long before the sign-out the warning shows. Shorter timeouts warn at the halfway point. */
export const IDLE_WARNING_MS = 2 * 60 * 1000;
export const IDLE_WARNING_TOAST_ID = "idle-timeout-warning";

export const idleWarningMessage = (minutesLeft: number): string =>
  `You'll be signed out in ${minutesLeft} minute${minutesLeft === 1 ? "" : "s"} due to ` +
  "inactivity. Click or press any key to stay signed in.";

/**
 * Ends the session after `minutes` without user activity. Unset or 0 disables it.
 * Returns an uninstall.
 */
export function installIdleTimeout(
  minutes = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES)
): () => void {
  if (!(minutes > 0)) return () => {};
  const timeoutMs = minutes * 60 * 1000;
  const warningMs = Math.min(IDLE_WARNING_MS, timeoutMs / 2);
  let lastActivityAt = 0;
  let lastWriteAt = 0;
  let warningShown = false;

  const hideWarning = (): void => {
    if (!warningShown) return;
    warningShown = false;
    try {
      toast.dismiss(IDLE_WARNING_TOAST_ID);
    } catch {
      // The warning is a courtesy; it must never affect the sign-out.
    }
  };

  const showWarning = (idleMs: number): void => {
    if (warningShown) return;
    warningShown = true;
    try {
      toast.warning(idleWarningMessage(Math.max(1, Math.ceil((timeoutMs - idleMs) / 60000))), {
        toastId: IDLE_WARNING_TOAST_ID,
        autoClose: false,
        closeOnClick: true,
      });
    } catch {
      // The warning is a courtesy; it must never affect the sign-out.
    }
  };

  const record = (now: number): void => {
    lastActivityAt = now;
    hideWarning();
    if (now - lastWriteAt < WRITE_THROTTLE_MS) return;
    lastWriteAt = now;
    try {
      tokenStorage().setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // Blocked storage falls back to this tab's activity.
    }
  };

  const readSaved = (): number => {
    try {
      return Number(tokenStorage().getItem(LAST_ACTIVITY_KEY)) || 0;
    } catch {
      // Blocked storage falls back to this tab's activity.
      return 0;
    }
  };

  // `warn` is off when activity follows right away, so the warning isn't shown and
  // dismissed in the same tick.
  const check = (now: number, warn: boolean): void => {
    if (!peekSessionToken()) return hideWarning();
    // A recording counts as activity, so the idle clock starts when it ends.
    if (isRecordingHeld()) return record(now);
    const idleMs = now - Math.max(lastActivityAt, readSaved());
    if (idleMs >= timeoutMs) {
      hideWarning();
      endIdleSession();
    } else if (idleMs >= timeoutMs - warningMs) {
      if (warn) showWarning(idleMs);
    } else {
      // Activity in another window sharing the token moved the deadline back.
      hideWarning();
    }
  };

  const onActivity = (): void => {
    if (document.visibilityState === "hidden") return;
    const now = Date.now();
    // Scrolling fires constantly; activity within the last second can't have gone idle.
    if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) return;
    // Background tabs throttle timers, so returning to a tab must not count as activity
    // before the idle check has had its chance.
    check(now, false);
    record(now);
  };

  // A reload must not revive a session that already went idle. The app hasn't subscribed
  // to session ends yet, so flag it for this load's session-expired page.
  const saved = readSaved();
  if (saved && Date.now() - saved >= timeoutMs && peekSessionToken()) {
    clearSessionToken();
    markSessionExpiredOnLoad();
  }
  record(Date.now());
  ACTIVITY_EVENTS.forEach((type) => document.addEventListener(type, onActivity, true));
  const interval = window.setInterval(() => check(Date.now(), true), CHECK_INTERVAL_MS);

  return () => {
    window.clearInterval(interval);
    hideWarning();
    ACTIVITY_EVENTS.forEach((type) => document.removeEventListener(type, onActivity, true));
  };
}
